import { useCallback, useEffect, useRef, useState } from 'react';
import { Anchor, Baby, Compass, Newspaper, Utensils, Waves } from 'lucide-react';
import { PERSONAS } from '../lib/personas.js';

// Guest-type glyphs on the pills so the row scans as "pick your trip" rather
// than six text chips. Foodie shares the Utensils mark with the Dining
// section on purpose; Everyone gets the masthead/almanac newspaper.
const PERSONA_ICONS = {
  everyone: Newspaper,
  family: Baby,
  beachbum: Waves,
  foodie: Utensils,
  boater: Anchor,
  firsttimer: Compass
};

export default function PersonaToggle({ value, onChange }) {
  const active = PERSONAS.find((p) => p.id === value);
  const scrollerRef = useRef(null);
  // On narrow screens the pill row scrolls; without an affordance the hidden
  // personas ("On the Water", "First-Timer") just look cut off.
  const [moreRight, setMoreRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 8);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);
  // Mobile sticky budget (review 2026-06-12): the full card + header + tagline
  // stuck ~196px of a 844px viewport. Below sm the sticky element is just the
  // chip row (~52px); the hairlines, kicker, and tagline are sm+ only.
  //
  // No card at sm+ (design review 2026-09-26, finding 5). It was a white,
  // bordered, shadowed box, and white is for form inputs only (DECISIONS
  // 2026-09-20). A hairline above opens the row; the plan's own section rule,
  // directly below, closes it. A second hairline under the pills stacked with
  // that rule into three rules in about 100px.
  return (
    <section
      id="tailor-brief"
      className="sticky top-0 z-20 -mx-4 mb-8 bg-cream-50/95 px-4 pb-2 pt-2 backdrop-blur supports-[backdrop-filter]:bg-cream-50/80 sm:-mx-6 sm:mb-4 sm:px-6 sm:pb-4 sm:pt-3"
    >
      <div className="mx-auto max-w-brief">
        <div className="sm:border-t sm:border-navy-100 sm:pt-4">
          <div className="mb-3 hidden items-baseline justify-between gap-3 sm:flex">
            {/* Inter, not the display serif, so the kicker matches the Eyebrow elsewhere.
                No side padding: it sat 4px inside the column edge every other
                kicker on the page starts at, a leftover from the old card. */}
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-cream-600">
              Tailor today's brief
            </p>
            <p className="font-serif text-xs text-navy-600">
              {active?.tagline}
            </p>
          </div>
          <div className="relative">
            {/* A group of in-place filter toggles, not a tablist: there is no
                tabpanel to navigate into, so role=group + aria-pressed states
                the truth without promising arrow-key panel navigation.
                Phones scroll the row. At sm+ all six fit on one line: 13px
                labels and tighter spacing measure 654px in the 680px row
                (at 14px it was 752px, and "First-Timer" was cut off under the
                fade on every desktop). flex-wrap is the fallback, so a wider
                font drops a pill to a second line rather than clipping it. */}
            <div
              ref={scrollerRef}
              onScroll={checkOverflow}
              role="group"
              aria-label="Tailor today's brief by guest type"
              className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] sm:flex-wrap sm:gap-1 [&::-webkit-scrollbar]:hidden"
            >
              {PERSONAS.map((p) => {
                const isActive = p.id === value;
                const Icon = PERSONA_ICONS[p.id];
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => onChange(p.id)}
                    className={[
                      'inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium tracking-tight transition-all duration-200 sm:gap-1 sm:px-3 sm:text-[13px]',
                      isActive
                        ? 'bg-cream-500 text-navy-950 shadow-[0_2px_8px_rgba(207,152,68,0.35)]'
                        : 'bg-cream-100 text-navy-700 hover:bg-cream-200 hover:text-navy-900'
                    ].join(' ')}
                  >
                    {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                    {p.label}
                  </button>
                );
              })}
            </div>
            {moreRight && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 -right-1 w-10 bg-gradient-to-l from-cream-50 to-transparent"
              />
            )}
          </div>
          {/* Mobile-only confirmation: the sm header's tagline is hidden, so a
              pill tap would otherwise change only the off-screen card above the
              pinned toggle. This line updates in place, right under the pills. */}
          <p className="mt-1.5 font-serif text-xs text-navy-600 sm:hidden">
            {active?.tagline}
          </p>
        </div>
      </div>
    </section>
  );
}
